import { Modal } from '../../../components/Modal';
import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  FETCH_INTERMEDIATE_MITIGATION,
  UPDATE_INTERMEDIATE_MITIGATION,
} from '../queries';
import { ColumnsType } from 'antd/es/table';
import { ExternalIntermediateForm } from './forms/ExternalIntermediateForm';
import FinancialData from '../../../components/common/FinancialData';
import TableV3, {
  ModifyVariablesArgs,
} from '../../../components/generic/Table/TableV3';
import { tableSortingMethods } from '../../../utils';
import DependenciesTitle from '../../../components/common/DependenciesTitle';
import QueryErrorBoundary from '../../../components/error-handling';
import PercentageFormatter from '../../../components/common/PercentageFormatter';

type Location = {
  delay: number;
  vendorName: string;
  percentage: number;
  uuid: string;
};

type ParsedLocation = {
  delay: number;
  vendorName: string;
  percentage: number;
  key: string;
};

type LocationSupported = {
  locationName: string;
  uuid: string;
};

type Division = {
  name: string;
  uuid: string;
};

type Intermediate = {
  uuid: string;
  division: Division;
  vendors: Location[];
  locationSupported: LocationSupported;
  location: LocationSupported;
  name: string;
  revenue: number;
};

type IntermediateMitigationOptionDashboard = {
  intermediate: Intermediate;
};

interface DataReturned {
  intermediateMitigationOptionDashboard: IntermediateMitigationOptionDashboard[];
}

interface ExternalIntermediateProps {
  division: string;
  location: string;
}

export interface TableData {
  uuid: string;
  locationSupported: string;
  vendors: Location[];
  locationSupportedUuid: string;
  revenue: number;
}

export interface ConvertedTableData {
  uuid: string;
  locationSupported: string;
  vendors: ParsedLocation[];
  locationSupportedUuid: string;
  revenue: number;
}

const uniqueById = <T extends Location>(arr: T[]): T[] => {
  return Array.from(new Map(arr.map((item) => [item.uuid, item])).values());
};

export default function ExternalIntermediate({
  division,
  location,
}: Readonly<ExternalIntermediateProps>) {
  const [item, setItem] = useState<TableData>();
  const [modalOpen, setModalOpen] = useState(false);

  const { data, loading, error } = useQuery<DataReturned>(
    FETCH_INTERMEDIATE_MITIGATION,
    {
      variables: {
        division: { uuid: division },
        location: { uuid: location },
        intermediateType: 'External',
      },
    },
  );

  const flattenedIntermediateVendors = [
    ...new Set(
      data?.intermediateMitigationOptionDashboard.map((item) =>
        item.intermediate.vendors.map((vendor) => vendor.uuid),
      )[0],
    ),
  ];

  const [
    UpdateIntermediateMitigation,
    { loading: updateExternalMitigationLoading },
  ] = useMutation(UPDATE_INTERMEDIATE_MITIGATION, {
    refetchQueries: ['GetIntermediateMitigationMatrix'],
  });

  const handleCancel = () => {
    setModalOpen(false);
  };

  const convertData = (data: DataReturned): ConvertedTableData[] => {
    if (data.intermediateMitigationOptionDashboard.length === 0) return [];
    return data.intermediateMitigationOptionDashboard.map((item) => {
      return {
        uuid: item.intermediate.uuid,
        locationSupported: item.intermediate.locationSupported.locationName,
        vendors: uniqueById(item.intermediate.vendors).map(
          ({ uuid: key, ...rest }) => ({
            key,
            ...rest,
          }),
        ),
        locationSupportedUuid: item.intermediate.locationSupported.uuid,
        revenue: item.intermediate.revenue,
      };
    });
  };

  const locationColumns: ColumnsType<TableData> = uniqueById(
    data?.intermediateMitigationOptionDashboard[0]?.intermediate?.vendors || [],
  ).map((vendor, index: number) => ({
    title: <DependenciesTitle name={vendor.vendorName} />,
    dataIndex: 'vendors',
    key: vendor.uuid,
    width: 150,
    align: 'right',
    colIndex: index,
    editable: true,
    sorter: (a, b) =>
      tableSortingMethods.number(
        a.vendors?.find((loc) => loc.uuid === vendor.uuid)?.percentage || 0,
        b.vendors?.find((loc) => loc.uuid === vendor.uuid)?.percentage || 0,
      ),
    showSorterTooltip: false,
    render: (_, record) => {
      const currentVendor = record.vendors?.find(
        (loc) =>
          (loc as Location & Pick<ParsedLocation, 'key'>).key === vendor.uuid,
      );
      return (
        <div className="custom-table--column">
          <PercentageFormatter value={currentVendor?.percentage} />
          <span
            className={`${currentVendor?.delay === 0 ? 'text-[#CDCDCD]' : ''}`}
          >
            {currentVendor?.delay}
            {' mths'}
          </span>
        </div>
      );
    },
  }));

  const columns: ColumnsType<TableData> = [
    {
      title: '',
      dataIndex: 'locationSupported',
      key: 'locationSupported',
      width: 220,
      fixed: 'left',
      sorter: (a, b) =>
        tableSortingMethods.string(a.locationSupported, b.locationSupported),
      render: (text: string) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
    {
      title: 'Revenue Supported Annually',
      dataIndex: 'revenue',
      key: 'revenue',
      width: 110,
      fixed: 'left',
      align: 'right',
      sorter: (a, b) => tableSortingMethods.number(a.revenue, b.revenue),
      render: (text: number) => (
        <span className="custom-table--column custom-table--column-revenue">
          {<FinancialData text={text} />}
        </span>
      ),
    },
    ...locationColumns,
  ];

  const modifyVariables = ({ currentRecord }: ModifyVariablesArgs) => {
    const commonValues = {
      division: { uuid: division },
      location: {
        uuid: location,
      },
      intermediateType: 'External',
      intermediate: { uuid: currentRecord.uuid },
      mutation: UpdateIntermediateMitigation,
    };

    return {
      ...commonValues,
    };
  };

  return (
    <div>
      <QueryErrorBoundary error={error}>
        <TableV3
          title="Intermediates Mitigation"
          columns={columns}
          data={data ? convertData(data) : []}
          loading={loading || updateExternalMitigationLoading}
          editable
          modifyVariables={modifyVariables}
          customStyles="custom-table--dependency-mapping"
          inlineEditing
          editableOptions={{
            update: true,
          }}
          isMatrix
          isMitigation
          scroll={{ x: 800, y: 340 }}
          mitigatedLocations={flattenedIntermediateVendors}
        />
      </QueryErrorBoundary>
      <Modal
        title="EXTERNAL MITIGATION"
        open={modalOpen}
        onClose={handleCancel}
      >
        <ExternalIntermediateForm
          data={item}
          onCancel={handleCancel}
          division={division}
          location={location}
        />
      </Modal>
    </div>
  );
}
